<template>
  <div class="day-view h-full">
    <div class="day-header grid grid-cols-1 border-b">
      <div class="day-header-cell p-2 text-center font-medium">
        {{ formatDate(date) }}
        <span v-if="isHoliday(date)" class="text-red-500 ml-2"
          >(Holiday: {{ getHolidayName(date) }})</span
        >
      </div>
    </div>

    <div class="day-body grid grid-cols-1 h-full overflow-y-auto">
      <div
        v-for="hour in hours"
        :key="hour.value"
        class="hour-row flex border-b border-gray-100"
      >
        <div class="hour-label w-16 py-2 pr-2 text-right text-xs text-gray-500">
          {{ hour.label }}
        </div>

        <div
          class="hour-content flex-1 min-h-[60px] border-l border-gray-200 relative"
          @click="handleTimeSlotClick(hour.value)"
          @dblclick="$emit('dblclick-date', setHours(date, hour.value))"
        >
          <div
            v-for="(event, index) in getEventsForHour(hour.value)"
            :key="event.id"
            class="absolute rounded px-2 py-1 text-xs overflow-hidden cursor-move group relative"
            :class="dashboardStore.getEventColorClass(event)"
            :style="
              getEventPositionStyle(
                event,
                hour.value,
                index,
                getEventsForHour(hour.value).length,
              )
            "
            @click.stop="$emit('event-click', event)"
            draggable="true"
            @dragstart="handleDragStart($event, event)"
            @dragend="handleDragEnd"
          >
            <!-- Use slot if provided, otherwise use default rendering -->
            <slot
              name="event-tile"
              :event="event"
              :date="date"
              :view="'day'"
              :hour="hour.value"
              :deletable="deletable"
              :delete-event="(id) => $emit('event-delete', id)"
            >
              <!-- Delete button -->
              <button
                v-if="deletable"
                class="absolute top-0 right-0 bg-white rounded-full p-0.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                @click.stop="$emit('event-delete', event.id)"
              >
                <XMarkIcon class="h-3 w-3 text-red-500" />
              </button>

              <!-- Default event content -->
              <div class="flex flex-col">
                <div
                  v-if="eventTemplate.showTitle"
                  class="font-medium truncate"
                  :class="{
                    'line-clamp-1': eventTemplate.titleLines === 1,
                    'line-clamp-2': eventTemplate.titleLines === 2,
                  }"
                >
                  {{ event.title }}
                </div>

                <div
                  v-if="eventTemplate.showTime && event.time"
                  class="text-xs opacity-75"
                >
                  {{ event.time }}
                </div>

                <div
                  v-if="eventTemplate.showType && event.type"
                  class="text-xs opacity-75"
                >
                  {{ event.type }}
                </div>

                <div
                  v-if="eventTemplate.showStatus && event.status"
                  class="text-xs opacity-75"
                >
                  {{ event.status }}
                </div>

                <div
                  v-if="eventTemplate.showNotes && event.notes"
                  class="text-xs opacity-75 mt-1"
                  :class="{
                    'line-clamp-1': eventTemplate.notesLines === 1,
                    'line-clamp-2': eventTemplate.notesLines === 2,
                  }"
                >
                  {{ event.notes }}
                </div>
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDashboardStore } from "@/store/dashboard";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { showToast } from "@/composables/swal";
import { format, isSameDay, parseISO, setHours, setMinutes } from "date-fns";
import { computed, ref } from "vue";
import { OrderStatus } from "@/composables";
const dashboardStore = useDashboardStore();

interface Event {
  id: string;
  title: string;
  start: string;
  end?: string;
  time?: string;
  status: OrderStatus;
  type?: string;
  [key: string]: any;
}

interface Holiday {
  id: string;
  name: string;
  date: string;
  type: string;
}

// Define props
const props = defineProps({
  date: {
    type: Date,
    required: true,
  },
  events: {
    type: Array as () => Event[],
    default: () => [],
  },
  holidays: {
    type: Array as () => Holiday[],
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: true,
  },
  deletable: {
    type: Boolean,
    default: true,
  },
  eventTemplate: {
    type: Object,
    default: () => ({
      showTitle: true,
      showTime: true,
      showType: false,
      showStatus: false,
      showNotes: false,
      titleLines: 1,
      notesLines: 0,
    }),
  },
});

// Define emits
const emit = defineEmits([
  "event-click",
  "time-slot-click",
  "event-drop",
  "event-delete",
  "dblclick-date",
]);

const draggedEvent = ref<Event | null>(null);
const draggedEventElement = ref<HTMLElement | null>(null);

const hours = computed(() => {
  const result = [];
  for (let i = 0; i < 24; i++) {
    result.push({
      value: i,
      label:
        i === 0
          ? "12 AM"
          : i < 12
            ? `${i} AM`
            : i === 12
              ? "12 PM"
              : `${i - 12} PM`,
    });
  }
  return result;
});

const formatDate = (date: Date) => {
  return format(date, "EEEE, MMMM d, yyyy");
};

const isHoliday = (date: Date) => {
  return props.holidays.some((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(holidayDate, date);
  });
};

const getHolidayName = (date: Date) => {
  const holiday = props.holidays.find((holiday) => {
    const holidayDate = parseISO(holiday.date);
    return isSameDay(holidayDate, date);
  });

  return holiday ? holiday.name : "";
};

const getEventsForHour = (hour: number) => {
  return props.events
    .filter((event) => {
      let eventHour = hour;
      if (event.time) {
        const [h] = event.time.split(":").map(Number);
        eventHour = h;
      } else if (event.start.includes("T")) {
        const eventDate = parseISO(event.start);
        eventHour = eventDate.getHours();
      }
      return eventHour === hour;
    })
    .map(
      (event) =>
        ({
          ...(event as Event),
          status: (event.status as OrderStatus) || OrderStatus.Pending,
        }) as Event,
    );
};

const getEventPositionStyle = (
  event: Event,
  hour: number,
  index: number,
  totalEvents: number,
) => {
  let minutes = 0;

  if (event.time) {
    const [, m] = event.time.split(":").map(Number);
    minutes = m;
  } else if (event.start.includes("T")) {
    const eventDate = parseISO(event.start);
    minutes = eventDate.getMinutes();
  }

  const top = (minutes / 60) * 100;
  const width = 100 / totalEvents;
  const left = index * width;

  return {
    top: `${top}%`,
    left: `${left}%`,
    width: `${width}%`,
    height: "50px",
  };
};

const handleTimeSlotClick = (hour: number) => {
  if (!props.editable) return;

  const newDate = new Date(props.date);
  newDate.setHours(hour);
  newDate.setMinutes(0);
  emit("time-slot-click", newDate);
};

const handleDragStart = (e: DragEvent, event: Event) => {
  if (!props.editable || !e.dataTransfer) return;

  // Check if the event is on a holiday date
  const eventDate = parseISO(event.start);
  if (isHoliday(eventDate)) {
    e.preventDefault();
    showToast("Cannot move events from holiday dates", "warning");
    return;
  }

  e.dataTransfer.effectAllowed = "move";
  e.dataTransfer.setData("text/plain", event.id);
  draggedEvent.value = event;
  draggedEventElement.value = e.target as HTMLElement;
};

const handleDragEnd = (e: DragEvent) => {
  if (!props.editable || !draggedEvent.value || !e.dataTransfer) return;

  // Check if the current date is a holiday
  if (isHoliday(props.date)) {
    showToast("Cannot move events to holiday dates", "warning");
    draggedEvent.value = null;
    draggedEventElement.value = null;
    return;
  }

  const hourElement = findClosestHourElement(e.clientY);
  if (hourElement) {
    const hourValue = parseInt(hourElement.dataset.hour || "0", 10);
    const minutePercentage = calculateMinutePercentage(e.clientY, hourElement);
    const minutes = Math.floor(minutePercentage * 60);

    const newDate = new Date(props.date);
    newDate.setHours(hourValue);
    newDate.setMinutes(minutes);

    emit("event-drop", draggedEvent.value.id, newDate);
  }

  draggedEvent.value = null;
  draggedEventElement.value = null;
};

const findClosestHourElement = (clientY: number) => {
  const hourElements = document.querySelectorAll(".hour-row");
  let closestElement = null;
  let closestDistance = Infinity;

  hourElements.forEach((element, index) => {
    const rect = element.getBoundingClientRect();
    const distance = Math.abs(rect.top + rect.height / 2 - clientY);

    if (distance < closestDistance) {
      closestDistance = distance;
      closestElement = element;
      (closestElement as HTMLElement).dataset.hour = index.toString();
    }
  });

  return closestElement as HTMLElement | null;
};

const calculateMinutePercentage = (
  clientY: number,
  hourElement: HTMLElement,
) => {
  const rect = hourElement.getBoundingClientRect();
  const relativeY = clientY - rect.top;
  return Math.max(0, Math.min(1, relativeY / rect.height));
};
</script>

<style scoped>
.day-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.day-body {
  flex: 1;
  overflow-y: auto;
}

.hour-content {
  position: relative;
}
</style>
